import {
  type Action,
  FilterActionTypes,
  type Thunk,
  action,
  thunk,
} from "easy-peasy";
import set from "lodash/set";

import type { ByDate, ByPeople, ByRestaurantId } from "./types";
import { Logger } from "../dependencies/logger";
import { wait } from "../dependencies/utils";
import getAllAvailabilitySearch, {
  type AvailabilitySearchWithOptions,
  type GetAllAvailabilitySearchParams,
} from "../gql/getters/allAvailabilitySearch";
import cancelAvailabilitySearch from "../gql/setters/cancelAvailabilitySearch";
import { AbortError, type LogEffect, createLogEffect } from "../utils/store";

const logEffect = (
  property: keyof FilterActionTypes<AllAvailabilitySearchModel>,
) =>
  createLogEffect<AllAvailabilitySearchModel>(
    "ALL_AVAILABILITY_SEARCH",
    property,
    false,
  );

const logger = new Logger("RESTAURANT_STORE - ALL_AVAILABILITY_SEARCH", "LOG");
// logger.enable();

const POLLING_INTERVAL = 1000;

async function getAllAvailabilitySearchUntilSynced(
  params: GetAllAvailabilitySearchParams,
) {
  const allAvailabilitySearch = await getAllAvailabilitySearch(params);

  const done = allAvailabilitySearch.every(
    (availabilitySearch) =>
      availabilitySearch.dataStatus === "CACHED" ||
      availabilitySearch.dataStatus === "STALE",
  );

  if (done) {
    return allAvailabilitySearch;
  }

  await wait(POLLING_INTERVAL);

  return getAllAvailabilitySearchUntilSynced(params);
}

export type AllAvailabilitySearchModel = {
  byId: ByRestaurantId<ByDate<ByPeople<AvailabilitySearchWithOptions>>>;
  byIdOnChange: LogEffect<AllAvailabilitySearchModel>;
  clear: Action<AllAvailabilitySearchModel>;
  set: Action<
    AllAvailabilitySearchModel,
    {
      date: string;
      people: number;
      availability: AvailabilitySearchWithOptions;
    }
  >;
  merge: Action<
    AllAvailabilitySearchModel,
    {
      date: string;
      people: number;
      availability: AvailabilitySearchWithOptions[];
    }
  >;
  loading: boolean;
  loadingOnChange: LogEffect<AllAvailabilitySearchModel>;
  setLoading: Action<AllAvailabilitySearchModel, boolean>;
  fetch: Thunk<
    AllAvailabilitySearchModel,
    { restaurantId: number; people: number; date: string },
    undefined,
    AllAvailabilitySearchModel,
    Promise<AvailabilitySearchWithOptions>
  >;
  fetchAll: Thunk<
    AllAvailabilitySearchModel,
    {
      restaurantIds: number[];
      people: number;
      date: string;
      fetchAbortSignal: AbortSignal;
      cancelSearchAbortController?: AbortController;
    }
  >;
};

const allAvailabilitySearchModel: AllAvailabilitySearchModel = {
  byId: {},
  byIdOnChange: logEffect("byId"),
  clear: action((state) => {
    if (Object.keys(state.byId).length === 0) {
      return;
    }
    logger.log("Clear");
    state.byId = {};
  }),
  set: action((state, payload) => {
    logger.log("Set", payload);

    const id = payload.availability.restaurantId;

    if (id) {
      set(state.byId, [id, payload.date, payload.people], payload.availability);
    }
  }),
  merge: action((state, payload) => {
    logger.log("Merge", payload);

    for (const availability of payload.availability) {
      const id = availability.restaurantId;

      if (id) {
        set(state.byId, [id, payload.date, payload.people], availability);
      }
    }
  }),
  loading: false,
  loadingOnChange: logEffect("loading"),
  setLoading: action((state, payload) => {
    logger.log("Set loading", payload);

    state.loading = payload;
  }),
  fetch: thunk(async (actions, payload) => {
    logger.log("Fetch", payload);

    const allAvailabilitySearch = await getAllAvailabilitySearchUntilSynced({
      restaurantIds: [payload.restaurantId],
      people: payload.people,
      date: payload.date,
    });

    const availability = allAvailabilitySearch[0]; // TODO: sometimes the response has multiple sets of results for a single restaurant, we might need to merge them?

    if (availability) {
      actions.set({
        date: payload.date,
        people: payload.people,
        availability,
      });
    }

    return availability;
  }),
  fetchAll: thunk(async (actions, payload) => {
    if (payload.fetchAbortSignal.aborted) {
      return;
    }

    const count = payload.restaurantIds.length;
    logger.log(`Fetching availability (${count})...`);

    actions.setLoading(true);

    // remove the previous `fetchAbortSignal` event listener (by aborting the `cancelSearchAbortController`)
    if (
      payload.cancelSearchAbortController &&
      !payload.cancelSearchAbortController.signal.aborted
    ) {
      payload.cancelSearchAbortController.abort(
        new AbortError("Remove previous fetchAbortSignal event listener"),
      );
    }

    const cancelSearchAbortController = new AbortController();

    payload.fetchAbortSignal.addEventListener(
      "abort",
      () => {
        logger.log(`Fetch availability aborted (${count})`);

        cancelAvailabilitySearch(
          {
            restaurantIds: payload.restaurantIds,
            people: payload.people,
            date: payload.date,
          },
          cancelSearchAbortController.signal,
        );

        actions.setLoading(false);
      },
      {
        once: true, // trigger once at most
        signal: cancelSearchAbortController.signal, // this removes the event listener if the `cancelSearchAbortController` is aborted
      },
    );

    const allAvailabilitySearch = await getAllAvailabilitySearch(
      {
        restaurantIds: payload.restaurantIds,
        people: payload.people,
        date: payload.date,
      },
      payload.fetchAbortSignal,
    );

    // TODO: not all restaurants are present in the response, e.g. ID 77 in Frankton
    // TODO: @ben do we need to refetch the missing ids as well as the unsynced ids?
    const unsyncedIds = allAvailabilitySearch
      .filter(({ dataStatus }) => dataStatus === "NEW")
      .map(({ restaurantId }) => restaurantId)
      .filter((id): id is number => id !== null);

    actions.merge({
      people: payload.people,
      date: payload.date,
      availability: allAvailabilitySearch,
    });

    // start polling for unsynced restaurants
    if (unsyncedIds.length > 0) {
      await new Promise<void>((resolve) => {
        const fetchUnsyncedTimeout = setTimeout(async () => {
          await actions.fetchAll({
            restaurantIds: unsyncedIds,
            people: payload.people,
            date: payload.date,
            fetchAbortSignal: payload.fetchAbortSignal,
            cancelSearchAbortController,
          });

          resolve();
        }, POLLING_INTERVAL);

        payload.fetchAbortSignal.addEventListener("abort", () => {
          clearTimeout(fetchUnsyncedTimeout);

          resolve();
        });
      });
    } else {
      if (!cancelSearchAbortController.signal.aborted) {
        cancelSearchAbortController.abort(
          new AbortError("Remove previous fetchAbortSignal event listener"),
        );
      }

      logger.log(`Fetch availability done (${count})`);

      actions.setLoading(false);
    }
  }),
};

export default allAvailabilitySearchModel;
